import { config } from '../config';
import { approvalHeaders, readAdminError, type Approval } from './approvalRequests';
import { getAdminApiAuthHeaders } from './auth';

export interface Experiment {
  id: string;
  name: string;
  description: string;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  traffic_percentage: number;
  target_audience?: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface ExperimentVariant {
  name: string;
  weight: number;
  is_control: boolean;
  config: Record<string, unknown>;
}

export const experimentsApi = {
  async list(status?: string): Promise<Experiment[]> {
    const headers = await getAdminApiAuthHeaders();
    const url = new URL(`${config.api.url}/admin/experiments`);
    if (status) url.searchParams.set('status', status);
    const res = await fetch(url.toString(), { headers });
    if (!res.ok) throw new Error('Failed to list experiments');
    const data = await res.json();
    return data.items || [];
  },

  async get(id: string): Promise<Experiment> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/experiments/${id}`, { headers });
    if (!res.ok) throw new Error('Failed to get experiment');
    return res.json();
  },

  async create(experiment: {
    name: string;
    description: string;
    trafficPercentage: number;
    targetAudience?: string;
    tags?: string[];
    variants: ExperimentVariant[];
  }): Promise<Experiment> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/experiments`, {
      method: 'POST',
      headers,
      body: JSON.stringify(experiment),
    });
    if (!res.ok) throw new Error('Failed to create experiment');
    return res.json();
  },

  async update(
    id: string,
    updates: Partial<{
      name: string;
      description: string;
      trafficPercentage: number;
      targetAudience: string;
      tags: string[];
    }>
  ): Promise<Experiment> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/experiments/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });
    if (!res.ok) throw new Error('Failed to update experiment');
    return res.json();
  },

  // DM-6437: activate and archive are FM-105 destructive operations, so they
  // carry the reason + approval headers a second operator's approval yields.
  async activate(id: string, approval: Approval): Promise<Experiment> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/experiments/${id}/activate`, {
      method: 'POST',
      headers: { ...headers, ...approvalHeaders(approval) },
    });
    if (!res.ok) throw await readAdminError(res, 'Failed to activate experiment');
    return res.json();
  },

  async archive(id: string, approval: Approval): Promise<void> {
    const headers = await getAdminApiAuthHeaders();
    const res = await fetch(`${config.api.url}/admin/experiments/${id}`, {
      method: 'DELETE',
      headers: { ...headers, ...approvalHeaders(approval) },
    });
    if (!res.ok) throw await readAdminError(res, 'Failed to archive experiment');
  },
};
